import { t } from "@travily/i18n";
import { Select } from "@ui/components/Select";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { CurrencyChangeConfirmModal } from "@/components/CurrencyChangeConfirmModal/CurrencyChangeConfirmModal";

const COLOR = "green" as const;

/** One supported selling country the seller can pick, with its payout currency. */
interface SupportedCountry {
  code: string;
  name: string;
  currency: string;
}

export interface ServiceSellingCountryStepViewProps {
  /** Current stage index for the wizard progress indicator. */
  currentStage: number;
  /** Total number of stages in the wizard. */
  totalStages: number;
  /** Countries the seller can pick from (code, display name, payout currency). */
  supportedCountries: SupportedCountry[];
  /** Selected country code, or an empty string when nothing is picked yet. */
  value: string;
  /** Field-level validation error to surface beneath the country picker. */
  error?: string;
  /** Seller's current selling currency — the "from" side of a re-price. */
  currentCurrency: string;
  /** How many existing paid drafts a currency change would re-price. */
  repriceableCount: number;
  /** Whether the confirm-before-reprice modal is open. */
  confirmOpen: boolean;
  /** Whether the primary (continue) action is in flight. */
  primaryPending: boolean;
  /** Tertiary top-bar actions (e.g. "Questions?" / "Finish later"). */
  topBarActions?: ReactNode;
  /** Navigate to the previous stage. Omitted on the first stage. */
  onBack?: () => void;
  /** Submit the current stage; may open the confirm modal instead of advancing. */
  onPrimary: () => void;
  /** Called when the seller picks a country (empty string when cleared). */
  onChange: (country: string) => void;
  /** Open/close the confirm-before-reprice modal. */
  onConfirmOpenChange: (open: boolean) => void;
  /** Confirm the currency change and advance. */
  onConfirm: () => void;
}

export function ServiceSellingCountryStepView({
  currentStage,
  totalStages,
  supportedCountries,
  value,
  error,
  currentCurrency,
  repriceableCount,
  confirmOpen,
  primaryPending,
  topBarActions,
  onBack,
  onPrimary,
  onChange,
  onConfirmOpenChange,
  onConfirm,
}: ServiceSellingCountryStepViewProps) {
  const selected = supportedCountries.find((entry) => entry.code === value);

  return (
    <>
      <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
        {topBarActions}
        <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
          {t("common.actions.continue")}
        </Wizard.PrimaryButton>
        <Wizard.Stage
          heading={t("web.services.creator.steps.selling_country.show.title")}
          helper={t("web.services.creator.steps.selling_country.show.subtitle")}
        >
          <div className="w-full max-w-[600px] text-left">
            <Select
              variant="wizard"
              isLabelHidden
              label={t("web.services.creator.steps.selling_country.show.country_label")}
              placeholder={t("web.services.creator.steps.selling_country.show.country_placeholder")}
              selectedKey={value || null}
              onSelectionChange={(key) => onChange(key ? String(key) : "")}
              isInvalid={Boolean(error)}
              errorMessage={error}
            >
              {supportedCountries.map((entry) => (
                <Select.Item key={entry.code} id={entry.code} textValue={entry.name}>
                  {entry.name}
                </Select.Item>
              ))}
            </Select>
            {selected ? (
              <p className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-green-900 text-sm">
                {t("web.services.creator.steps.selling_country.show.currency_hint", { currency: selected.currency })}
              </p>
            ) : null}
          </div>
        </Wizard.Stage>
      </Wizard>
      <CurrencyChangeConfirmModal
        isOpen={confirmOpen}
        onOpenChange={onConfirmOpenChange}
        onConfirm={onConfirm}
        isPending={primaryPending}
        count={repriceableCount}
        fromCurrency={currentCurrency}
        toCurrency={selected?.currency ?? currentCurrency}
      />
    </>
  );
}
